import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, Clock } from "lucide-react";
import { frameworkCategories } from "../mock/frameworks";
import { useFetch } from "../lib/api";
import { SectionLabel } from "../components/shared";

function ComplexityDots({ n }) {
  return (
    <span className="inline-flex gap-1" title={`Complexity ${n}/5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} className={`w-1.5 h-1.5 rounded-full ${i <= n ? "bg-[#E8590C]" : "bg-[#E7E0D2]"}`} />
      ))}
    </span>
  );
}

export default function Library() {
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const { data: frameworks, loading } = useFetch("/frameworks");
  const shown = (frameworks || []).filter((f) => (cat === "All" || f.category === cat) && (f.name + f.full).toLowerCase().includes(q.toLowerCase()));
  return (
    <main className="max-w-[1320px] mx-auto px-5 lg:px-8 py-14" data-testid="library-page">
      <SectionLabel>The Framework Atlas</SectionLabel>
      <h1 className="font-display font-bold text-[38px] lg:text-[54px] tracking-tight">The risk framework <span className="text-[#3F4CE0]">encyclopedia.</span></h1>
      <p className="mt-4 text-[16px] text-[#1A1914]/60 max-w-2xl">26 deep-dive entries — complexity, certification, time-to-implement, pros and cons — curated for US practitioners.</p>
      <div className="mt-8 flex flex-col md:flex-row gap-4 md:items-center">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search frameworks…" data-testid="library-search"
          className="w-full md:w-80 px-4 py-2.5 text-[14px] bg-white border border-[#E7E0D2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#3F4CE0]/40 focus:border-[#3F4CE0]" />
        <div className="flex flex-wrap gap-2">
          {frameworkCategories.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`library-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`px-3.5 py-1.5 text-[13px] font-medium rounded-full border rp-btn ${cat === c ? "bg-[#1A1914] text-white border-[#1A1914]" : "bg-white text-[#1A1914]/70 border-[#E7E0D2] hover:border-[#1A1914]/40"}`}>{c}</button>
          ))}
        </div>
      </div>
      <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="library-grid">
        {shown.map((f) => (
          <Link key={f.slug} to={`/library/${f.slug}`} data-testid={`framework-card-${f.slug}`} className="rp-card bg-white border border-[#E7E0D2] rounded-2xl p-6 group flex flex-col">
            <div className="flex items-start justify-between">
              <span className="font-mono-label text-[10px] px-2 py-0.5 rounded-full bg-[#F3EEE4] text-[#1A1914]/55 border border-[#E7E0D2]">{f.category}</span>
              {f.certification && <span className="inline-flex items-center gap-1 font-mono-label text-[9.5px] text-[#0E9355]"><Award size={12} /> Certifiable</span>}
            </div>
            <h3 className="mt-4 font-display font-bold text-[22px] group-hover:text-[#3F4CE0] transition-colors">{f.name}</h3>
            <p className="text-[13px] text-[#1A1914]/50">{f.full}</p>
            <p className="mt-3 text-[13.5px] leading-relaxed text-[#1A1914]/65 line-clamp-3">{f.description}</p>
            <div className="mt-4 pt-4 border-t border-[#E7E0D2] flex items-center justify-between mt-auto">
              <ComplexityDots n={f.complexity} />
              <span className="inline-flex items-center gap-1 text-[12px] text-[#1A1914]/50"><Clock size={12} /> {f.timeToImplement}</span>
              <ArrowRight size={15} className="text-[#3F4CE0] group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
        ))}
      </div>
      {shown.length === 0 && !loading && <p className="mt-12 text-[15px] text-[#1A1914]/50">No frameworks match — try another search.</p>}
    </main>
  );
}
